import React, { useState, useEffect } from 'react';
import { GOLFER_FITTING_CTAS, GOLFER_FITTING_CTA_INTERVAL_MS } from '../utils/golfFittingCtas';

interface AIAnalysisProps {
  overview: string;
  hasFetched: boolean;
  loading: boolean;
  onOpenMessenger: () => void;
  showLongTextToggle?: boolean;
}

const buildCtaSequence = () => {
  const rest = [...GOLFER_FITTING_CTAS.slice(1)];

  for (let i = rest.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }

  return [GOLFER_FITTING_CTAS[0], ...rest];
};

const AIAnalysis: React.FC<AIAnalysisProps> = ({ overview, hasFetched, loading, onOpenMessenger, showLongTextToggle = true }) => {
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [ctaSequence, setCtaSequence] = useState<string[]>(() => buildCtaSequence());
  const [isExpanded, setIsExpanded] = useState(false);
  const safeOverview = typeof overview === 'string' ? overview : '';
  const normalizedOverview = safeOverview.replace(/<br\s*\/?>/gi, '\n\n');

  useEffect(() => {
    if (!loading) {
      return;
    }

    const interval = setInterval(() => {
      setLoadingTextIndex((prevIndex) => {
        const nextIndex = prevIndex + 1;

        if (nextIndex >= ctaSequence.length) {
          const nextSequence = buildCtaSequence();
          setCtaSequence(nextSequence);
          return 0;
        }

        return nextIndex;
      });
    }, GOLFER_FITTING_CTA_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [loading, ctaSequence.length]);

  useEffect(() => {
    if (loading) {
      const nextSequence = buildCtaSequence();
      setCtaSequence(nextSequence);
      setLoadingTextIndex(0);
    }
  }, [loading]);

  const shouldTruncate = showLongTextToggle && (normalizedOverview.length > 120 || normalizedOverview.split(/\s+/).length > 20);
  const previewText = shouldTruncate && !isExpanded
    ? `${normalizedOverview.slice(0, 180).trim()}...`
    : normalizedOverview;

  return (
    <div className="chat-interaction-box" style={{
      background: '#f7fafd',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      padding: '0.5rem 0.5rem 0rem 0.5rem',
      marginBottom: '0.75rem',
      border: hasFetched ? '1.5px solid #3a7bd5' : '1.5px solid #e0e0e0',
      minHeight: '50px',
      height: 'auto',
      overflow: 'auto'
    }}>
      <div className="prompt-display">
        <div style={{display: 'flex', alignItems: 'center', marginBottom: '0.35rem'}}>
          <span style={{fontWeight: 700, color: hasFetched ? '#3a7bd5' : 'rgb(58, 123, 213)', fontSize: '1.05rem', marginRight: '0.5rem'}}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" style={{verticalAlign: 'middle', marginRight: '0.3rem'}}>
              <circle cx="11" cy="11" r="11" fill="#3a7bd5"/>
              <text x="5" y="15" fontSize="10" fill="#fff" fontWeight="bold">AI</text>
            </svg>
            {hasFetched ? 'NeutralFit Analysis' : 'NeutralFit'}
          </span>
        </div>
        <div style={{whiteSpace: 'pre-line', color: '#222', fontSize: '1rem', lineHeight: 1.45}}>
          {loading ? (
            <div className="loading-spinner" style={{ textAlign: 'center', padding: '1rem 0' }}>
              <div className="spinner"></div>
              <p>{ctaSequence[loadingTextIndex]}</p>
            </div>
          ) : (
            <>
              {shouldTruncate ? previewText : normalizedOverview}
              {shouldTruncate && (
                <button
                  type="button"
                  onClick={() => setIsExpanded((prev) => !prev)}
                  style={{
                    display: 'block',
                    marginTop: '0.35rem',
                    border: 'none',
                    background: 'transparent',
                    color: '#1d4ed8',
                    padding: 0,
                    cursor: 'pointer',
                    fontSize: '0.88rem',
                  }}
                >
                  {isExpanded ? 'Show Less' : 'Show More ...'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIAnalysis;
